import React, { useContext } from 'react';

import { hot } from 'react-hot-loader/root';

import { DevelopersCompanyContext } from 'common/containers/DevelopersCompanyContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import IdentifyAuthenticateLinkSnippet from 'common/developers/docs/sdk/snippets/IdentifyAuthenticateLinkSnippet';
import IdentifyAuthenticateLinksSnippet from 'common/developers/docs/sdk/snippets/IdentifyAuthenticateLinksSnippet';
import IdentifyCallbackSnippet from 'common/developers/docs/sdk/snippets/IdentifyCallbackSnippet';
import MultiLanguageCodeBlock from 'common/developers/docs/widget/MultiLanguageCodeBlock';
import Helmet from 'common/helmets/Helmet';

import 'css/components/developers/docs/install/_IdentifyDocs.scss';

const IdentifyAdvancedDocs = () => {
  const company = useContext(DevelopersCompanyContext);
  const viewer = useContext(ViewerContext);

  return (
    <div className="identifyDocs">
      <Helmet
        title="Advanced Install Docs | Canny"
        description="Install our JavaScript SDK to identify your users. This way, when they leave feedback on Canny, it'll be tied to their existing user account in your application."
        image="https://canny.io/images/canny-social.png"
        url="https://canny.io/docs/install/advanced"
      />
      <h1 className="heading">Advanced Identify Features</h1>
      <section className="step">
        <h2 className="subheading">Identify callback</h2>
        <p className="description">
          Once your users have been successfully identified, it can sometimes makes sense to perform
          a followup action. Identify accepts a callback argument which allows you to call a
          function when identify has completed.
        </p>
        <p className="description">
          The callback will called regardless of if identification was successful or it failed, and
          does not pass any arguments.
        </p>

        {!company?._id || !viewer || viewer.loggedOut ? (
          <p className="description">
            <span className="bold">To find your appID</span>, log in above. You'll see your appID
            pre&#8209;filled in the code&nbsp;snippet.
          </p>
        ) : null}

        <MultiLanguageCodeBlock
          blocks={[
            {
              code: company?._id
                ? IdentifyCallbackSnippet.replace('YOUR_APP_ID', company._id)
                : IdentifyCallbackSnippet,
              language: 'js',
            },
          ]}
          showSelector={false}
        />
      </section>
      <section className="step">
        <h2 className="subheading">Handling the routing to Canny yourself</h2>
        <p className="description">
          You may need to perform other actions when a user clicks on your feedback link prior to
          redirecting them to your Canny board.
        </p>
        <p className="description">To do this, you can use the authenticateCannyLink function.</p>

        <MultiLanguageCodeBlock
          blocks={[
            {
              code: company?.subdomain
                ? IdentifyAuthenticateLinkSnippet.replace(
                    'YOUR_FEEDBACK_LINK',
                    `"${company.primaryDomain || `${company.subdomain}.canny.io`}"`,
                  )
                : `// Where YOUR_FEEDBACK_LINK is a URL to your Canny site, eg, 'http://feedback.canny.io' \n${IdentifyAuthenticateLinkSnippet}`,
              language: 'js',
            },
          ]}
          showSelector={false}
        />
      </section>
      <section className="step">
        <h2 className="subheading">Authenticated Links to Canny</h2>
        <p className="description">
          By default, once a user has been identified using the Canny SDK, they will be
          automatically logged in when they navigate to a Canny page using a link in your app. This
          works because the SDK scans for Canny links and replaces them with secure, authenticated
          versions just before the user clicks them.
        </p>
        <p className="description">
          If you prefer more control over which links are authenticated, you can disable the{' '}
          <span className="code">authenticateLinks</span> setting. When this setting is disabled,
          only <span className="code">a</span> tags marked with the{' '}
          <span className="code">data-canny-link</span> attribute, or links that have been manually
          configured with
          <span className="code">authenticateCannyLink</span>, will be converted to authenticated
          links.
        </p>

        <MultiLanguageCodeBlock
          blocks={[
            {
              code: company?._id
                ? IdentifyAuthenticateLinksSnippet.replace('YOUR_APP_ID', company._id)
                : IdentifyAuthenticateLinksSnippet,
              language: 'js',
            },
          ]}
          showSelector={false}
        />
      </section>
    </div>
  );
};

export default hot(IdentifyAdvancedDocs);
